import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  Mail, 
  Phone, 
  Clock, 
  MessageCircle, 
  Send,
  Headphones,
  Building,
  Users,
  Sparkles,
  CheckCircle,
  Star,
  ArrowRight
} from 'lucide-react';
import { mockAPI } from '../mock';
import SEOHead from '../components/SEOHead';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
    contactMethod: 'email'
  });
  const [loading, setLoading] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [activeMethod, setActiveMethod] = useState(0);
  const { toast } = useToast();

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveMethod((prev) => (prev + 1) % contactMethods.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await mockAPI.submitContact(formData);
      toast({
        title: "Message Sent!",
        description: response.message,
      });
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: '',
        contactMethod: 'email'
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: Mail,
      title: "Email Support",
      description: "Get detailed help via email",
      contact: "support@brixbot.ai",
      responseTime: "Within 2 hours",
      color: "from-blue-400 to-cyan-500"
    },
    {
      icon: Phone,
      title: "Phone Support",
      description: "Talk to our experts directly",
      contact: "+421915721942",
      responseTime: "Mon-Fri 9AM-6PM CET",
      color: "from-violet-400 to-purple-500"
    },
    {
      icon: MessageCircle,
      title: "Live Chat",
      description: "Instant help via our chatbot",
      contact: "Available on website",
      responseTime: "24/7 availability",
      color: "from-green-400 to-emerald-500"
    }
  ];

  const supportFeatures = [
    {
      icon: Headphones,
      title: "24/7 Support",
      description: "Round-the-clock assistance whenever you need help with BrixBot.",
      color: "from-blue-400 to-cyan-500"
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Our AI specialists and customer success managers are here to help you succeed.",
      color: "from-violet-400 to-purple-500"
    },
    {
      icon: MessageCircle,
      title: "Multi-Channel",
      description: "Reach us via email, phone, live chat, or schedule a video call - your choice.",
      color: "from-green-400 to-emerald-500"
    }
  ];

  const faqItems = [
    {
      question: "How quickly can you set up my chatbot?",
      answer: "Our standard setup takes 3-5 business days. We'll work with you to train the AI on your data and customize it to your needs."
    },
    {
      question: "What if I need changes after setup?",
      answer: "We provide ongoing support and updates. Minor adjustments are included in your monthly plan, while major changes may require additional consultation."
    },
    {
      question: "Do you offer technical support?",
      answer: "Yes! We provide 24/7 technical support via email and live chat, with phone support available during business hours."
    },
    {
      question: "Can I integrate with my existing tools?",
      answer: "Absolutely! We support integrations with popular CRM systems, help desks, and business tools. Custom integrations are available too."
    }
  ];

  return (
    <div className="pt-16 bg-gray-950 overflow-hidden">
      <SEOHead 
        title="Contact Us - BrixBot AI Customer Service Support" 
        description="Get in touch with BrixBot support team. 24/7 customer service, expert AI consultation, and technical assistance. Email, phone, and live chat support available."
        isIndexable={true}
      />
      {/* Hero Section with Parallax */}
      <section className="relative py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-violet-950/30"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        ></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 hover:bg-violet-600/30 mb-8 text-lg py-2 px-6">
            <MessageCircle className="w-5 h-5 mr-2" />
            Get in Touch
          </Badge>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
            We're Here to
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 animate-pulse">
              Help You Succeed
            </span>
          </h1>
          
          <p className="text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
            Have questions about BrixBot? Need help getting started? 
            Our team of experts is ready to assist you every step of the way.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-32 bg-gray-900/50 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 to-purple-500/5"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-6">
              <Sparkles className="w-4 h-4 mr-2" />
              Multiple Ways to Connect
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Multiple Ways to Reach Us
            </h2>
            <p className="text-xl text-gray-300">
              Choose the method that works best for you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {contactMethods.map((method, index) => (
              <Card 
                key={index} 
                className={`bg-gray-900/50 border-violet-500/20 hover:bg-gray-900/70 hover:border-violet-400/40 transition-all duration-500 group backdrop-blur-sm ${
                  activeMethod === index ? 'ring-2 ring-violet-400/50 shadow-2xl shadow-violet-500/20' : ''
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <CardHeader className="text-center relative z-10">
                  <div className={`w-16 h-16 bg-gradient-to-br ${method.color} rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <method.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-white text-xl">{method.title}</CardTitle>
                  <CardDescription className="text-gray-400">
                    {method.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="text-center relative z-10">
                  <div className="text-violet-300 font-medium mb-2 text-lg">
                    {method.contact}
                  </div>
                  <div className="text-sm text-gray-400">
                    {method.responseTime}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-32 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-4">
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </Badge>
            <h2 className="text-4xl font-bold text-white mb-6">
              Send Us a Message
            </h2>
            <p className="text-xl text-gray-300">
              Fill out the form below and we'll get back to you within 2 hours during business hours.
            </p>
          </div>

          <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5"></div>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-purple-500"></div>
            
            <CardHeader className="relative z-10">
              <CardTitle className="text-white flex items-center text-2xl">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center mr-3">
                  <Send className="w-5 h-5 text-white" />
                </div>
                Contact Form
              </CardTitle>
              <CardDescription className="text-gray-400 text-lg">
                We'll respond to your inquiry as soon as possible
              </CardDescription>
            </CardHeader>
            
            <CardContent className="relative z-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-300">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 rounded-xl"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-300">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 rounded-xl"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-gray-300">Company</Label>
                  <div className="relative">
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Your company name"
                      className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 pl-12 rounded-xl"
                    />
                    <Building className="absolute left-4 top-3 w-4 h-4 text-gray-400" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-gray-300">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="What's this about?"
                    className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 rounded-xl"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-300">Message *</Label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="5"
                    placeholder="Tell us how we can help you..."
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-violet-500 focus:outline-none resize-none"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Preferred Contact Method</Label>
                  <div className="flex space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="contactMethod"
                        value="email"
                        checked={formData.contactMethod === 'email'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-violet-600 bg-gray-700 border-gray-600 focus:ring-violet-500"
                      />
                      <span className="text-gray-300">Email</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="contactMethod"
                        value="phone"
                        checked={formData.contactMethod === 'phone'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-violet-600 bg-gray-700 border-gray-600 focus:ring-violet-500"
                      />
                      <span className="text-gray-300">Phone</span>
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white py-6 text-lg rounded-xl shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-3" />
                      Send Message
                      <Send className="ml-3 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 bg-gray-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-6">
              <CheckCircle className="w-4 h-4 mr-2" />
              Common Questions
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-300">
              Quick answers to common questions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {faqItems.map((item, index) => (
              <Card key={index} className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm hover:border-violet-400/40 transition-all duration-500 group">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <CardHeader className="relative z-10">
                  <CardTitle className="text-white text-lg flex items-center">
                    <div className="w-8 h-8 bg-violet-500/20 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-violet-400 font-bold">{index + 1}</span>
                    </div>
                    {item.question}
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-gray-300 leading-relaxed">{item.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Support Team */}
      <section className="py-32 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-16">
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-6">
              <Star className="w-4 h-4 mr-2" />
              Support Promise
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our Support Promise
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We're committed to your success with dedicated support every step of the way.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {supportFeatures.map((feature, index) => (
              <Card key={index} className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm hover:border-violet-400/40 transition-all duration-500 group">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <CardHeader className="text-center relative z-10">
                  <div className={`w-20 h-20 bg-gradient-to-br ${feature.color} rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-10 h-10 text-white" />
                  </div>
                  <CardTitle className="text-white text-2xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-gray-300 leading-relaxed text-lg">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-r from-violet-900/30 to-purple-900/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-8 text-lg py-2 px-6">
            <Sparkles className="w-5 h-5 mr-2" />
            Ready to Connect?
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">
              Customer Service?
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Get started today with our expert team and see the difference BrixBot can make.
          </p>
          
          <div className="max-w-md mx-auto">
            <Button 
              size="lg" 
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-12 py-8 text-2xl rounded-3xl shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105"
            >
              <Sparkles className="w-6 h-6 mr-3" />
              Get Started Now
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;